import { useCallback, useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../components/common/PageTitle";
import { API_URL, DEFAULT_PER_PAGE } from "../../utilities/constants";
import FullPageLoader from "../../components/ui/FullPageLoader";
import { useUI } from "../../context/ui";
import ResultsPerPage from "../_common/ResultsPerPage";
import Pagination from "../../components/common/Pagination";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import {
  CiCalendar,
  CiChat2,
  CiCircleCheck,
  CiCircleRemove,
  CiMail,
  CiRedo,
  CiSearch,
  CiStreamOn,
  CiUser,
} from "react-icons/ci";
import { formatDateTime } from "../../utilities/helpers";
import PopUpModal from "../../components/common/PopUpModal";
import { Field, Form, Formik } from "formik";
import ButtonLightTextIcon from "../../components/ui/ButtonLightTextIcon";
import ButtonTertiary from "../../components/ui/ButtonTertiary";
import { MdConnectWithoutContact } from "react-icons/md";
import { RiApps2AddLine } from "react-icons/ri";
import {
  LuAppWindowMac,
  LuBadgeAlert,
  LuBadgeCheck,
  LuCalendarCheck,
  LuCalendarSearch,
  LuMailCheck,
  LuMessageCircleCode,
  LuRadio,
  LuUserRoundCheck,
} from "react-icons/lu";

const buildQueryParams = (params) =>
  Object.entries(params)
    .filter(([_, value]) => value !== "" && value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

const Label = ({ title, required }) => (
  <p className="color-primary f-w-600" style={{ paddingBottom: "5px" }}>
    {title}
    {required && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
  </p>
);

const InfoItem = ({ icon, label, name, showfulltext = false }) => (
  <div style={{ width: "100%" }}>
    <div className="flex center-left" style={{ gap: "10px", flexWrap: "wrap" }}>
      <div>{icon}</div>
      <div style={{ overflow: "hidden", flex: 1, minWidth: "120px" }}>
        <p
          className="f-w-300"
          style={{
            color: "#aaaaaa",
            fontSize: "10px",
            lineHeight: 1,
            paddingBottom: "2px",
          }}
        >
          {label}
        </p>
        <p
          style={{
            ...(showfulltext
              ? {
                  width: "100%",
                  lineHeight: 1.2,
                  borderRadius: "5px",
                  whiteSpace: "pre-wrap",
                  paddingRight: "10px",
                }
              : {
                  maxWidth: "190px",
                  lineHeight: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }),
            paddingBottom: "2px",
            fontSize: "14px",
          }}
        >
          {name}
        </p>
      </div>
    </div>
  </div>
);

const FeedBack = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [feedbackId, setFeedbackId] = useState(0);
  const [feedbackDetail, setFeedbackDetail] = useState(null);
  const [showFeedbackDetail, setShowFeedbackDetail] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const {
    setNewNotification,
    setShowNotification,
    addNotification,
    addToastMessage,
    setSegmentTitle,
  } = useUI();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const pageNo = searchParams.get("pageNo") || 0;
  const pageSize = searchParams.get("pageSize") || DEFAULT_PER_PAGE;

  const [filters, setFilters] = useState({
    fromDate: searchParams.get("fromDate") || "",
    toDate: searchParams.get("toDate") || "",
    category: searchParams.get("category") || "",
    feedbackStatus: searchParams.get("feedbackStatus") || "",
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const query = buildQueryParams({
      sortOrder: "desc",
      endDate: filters.toDate,
      startDate: filters.fromDate,
      pageNo,
      pageSize,
      feedbackStatus: filters.feedbackStatus,
      category: filters.category,
    });

    try {
      const response = await fetch(`${API_URL}/api/1.0.0/feedback?${query}`, {
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });
      const result = await response.json();
      setData(
        response.ok && result.result.responseCode === 200 ? result.data : null
      );
    } catch (e) {
      console.error("Error fetching data", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, pageNo, pageSize, filters]);

  const fetchFeedbackDetails = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/1.0.0/feedback/${id}`, {
        headers: {
          "Content-Type": "application/json",
          "X-JWT-Assertion": localStorage.getItem("token"),
        },
      });
      const result = await response.json();
      setFeedbackDetail(
        response.ok && result.result.responseCode === 200 ? result.data : null
      );
      setShowFeedbackDetail(true);
    } catch (e) {
      console.error("Error fetching feedback details", e);
      setFeedbackDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    setSegmentTitle("Feedback");
  }, [location, setSegmentTitle]);

  const handleSearch = useCallback(() => {
    const queryParams = buildQueryParams({
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      category: filters.category,
      feedbackStatus: filters.feedbackStatus,
      pageNo: 0,
      pageSize,
    });
    navigate(`/feedback?${queryParams}`);
  }, [filters, pageSize, navigate]);

  const resetFilters = useCallback(() => {
    setFilters({ fromDate: "", toDate: "", category: "", feedbackStatus: "" });
    navigate(`/feedback?pageNo=0&pageSize=${pageSize}`);
  }, [pageSize, navigate]);

  const filterOptions = useMemo(
    () => ({
      status: [
        { value: "", label: "Select Status" },
        { value: "1", label: "Unresolved" },
        { value: "2", label: "Resolved" },
      ],
      category: [
        { value: "", label: "Select Category" },
        { value: "1", label: "Transaction" },
        { value: "2", label: "App" },
        { value: "3", label: "General" },
      ],
    }),
    []
  );

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  return (
    <section
      className="pos-rel"
      style={{ padding: isMobile ? "10px" : "20px" }}
    >
      <PageTitle title="Feedback" />
      <FullPageLoader isActive={loading} />
      <section>
        <div
          className="flex center-left responsive-container"
          style={{
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "15px" : "12px",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <Label title="No. of Records" />
            <ResultsPerPage
              targetURL={`/feedback?${buildQueryParams({
                ...filters,
                pageNo: 0,
              })}&pageSize=`}
            />
          </div>
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <Label title="Status" />
            <select
              className="responsive-select"
              value={filters.feedbackStatus}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  feedbackStatus: e.target.value,
                }))
              }
              style={{ minWidth: "160px", width: isMobile ? "100%" : "auto" }}
            >
              {filterOptions.status.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <Label title="Category" />
            <select
              className="responsive-select"
              value={filters.category}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, category: e.target.value }))
              }
              style={{ minWidth: "160px", width: isMobile ? "100%" : "auto" }}
            >
              {filterOptions.category.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <Label title="From Date" />
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ width: isMobile ? "100%" : "auto" }}>
            <Label title="To Date" />
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
              style={{ width: "100%" }}
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "space-between" : "flex-start",
            }}
          >
            <div style={{ width: isMobile ? "100%" : "auto" }}>
              <Label title="&nbsp;" />
              <ButtonTextIcon
                type="button"
                onClick={handleSearch}
                icon={<CiSearch />}
              />
            </div>
            <div style={{ width: isMobile ? "100%" : "auto" }}>
              <Label title="&nbsp;" />
              <ButtonTextIcon
                type="button"
                onClick={resetFilters}
                icon={<CiRedo />}
              />
            </div>
          </div>
        </div>
        <div
          className="results"
          style={{ overflowX: isMobile ? "auto" : "visible" }}
        >
          <div className="rs-table-holder">
            {data?.feedbacks && (
              <table
                className="rs-table"
                style={{
                  minWidth: isMobile ? "800px" : "100%",
                  fontSize: isMobile ? "12px" : "14px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>#</th>
                    <th style={{ width: "15%" }}>Customer</th>
                    <th style={{ width: "10%" }}>Category</th>
                    <th style={{ width: "20%" }}>Subject</th>
                    <th style={{ width: "15%" }}>Query</th>
                    <th style={{ width: "15%" }}>Response</th>
                    <th style={{ width: "10%" }}>Status</th>
                    <th style={{ width: "10%" }}>Created At</th>
                    <th style={{ width: "10%" }}>Resolved At</th>
                    <th style={{ width: "10%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && data.feedbacks.length > 0
                    ? data.feedbacks.map((d, i) => (
                        <tr key={d.id || i}>
                          <td>{data.pageNo * data.pageSize + i + 1}</td>
                          <td>
                            <p
                              style={{
                                maxWidth: isMobile ? "100px" : "150px",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.user?.fullName || "-"}
                            </p>
                          </td>
                          <td>
                            {filterOptions.category[d.category]?.label || "-"}
                          </td>
                          <td>
                            <p
                              style={{
                                maxWidth: isMobile ? "120px" : "200px",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.subject || "-"}
                            </p>
                          </td>
                          <td>
                            <p
                              style={{
                                maxWidth: isMobile ? "100px" : "150px",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.description || "-"}
                            </p>
                          </td>
                          <td>
                            <p
                              style={{
                                maxWidth: isMobile ? "100px" : "150px",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.response || "-"}
                            </p>
                          </td>
                          <td>
                            <div
                              className="status active"
                              style={{
                                background:
                                  d.feedbackStatusID === 1
                                    ? "rgb(236, 9, 9)"
                                    : "#27ae60",
                                color: "white",
                                width: isMobile ? "80px" : "100px",
                                height: "30px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "9999px",
                                fontWeight: "bold",
                                fontSize: isMobile ? "12px" : "14px",
                              }}
                            >
                              {d.feedbackStatusID === 1
                                ? "Unresolved"
                                : "Resolved"}
                            </div>
                          </td>
                          <td>
                            {d.createdAt ? formatDateTime(d.createdAt) : "N/A"}
                          </td>
                          <td>
                            {d.updatedAt ? formatDateTime(d.updatedAt) : "N/A"}
                          </td>
                          <td>
                            <div
                              className="flex center-left"
                              style={{
                                gap: "12px",
                                flexDirection: isMobile ? "column" : "row",
                              }}
                            >
                              <ButtonTertiary
                                title="View"
                                actionType="view"
                                onClick={() => fetchFeedbackDetails(d.id)}
                              />
                              {d.feedbackStatusID === 1 && (
                                <ButtonTertiary
                                  title="Edit"
                                  actionType="edit"
                                  onClick={() => {
                                    setFeedbackId(d.id);
                                    setModalOpen(true);
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    : !loading && (
                        <tr>
                          <td
                            colSpan={10}
                            style={{ textAlign: "center", padding: "10px" }}
                          >
                            <p>No records found.</p>
                          </td>
                        </tr>
                      )}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {data?.feedbacks && (
          <Pagination
            totalRecords={data.totalRecords}
            pageNo={data.pageNo}
            pageSize={data.pageSize}
            baseUrl={`/feedback?${buildQueryParams(filters)}&`}
          />
        )}
        {isModalOpen && (
          <FeedbackModal
            id={feedbackId}
            onClose={() => setModalOpen(false)}
            setLoading={setLoading}
            callback={fetchData}
          />
        )}
        {showFeedbackDetail && feedbackDetail && (
          <PopUpModal>
            <section id="rs-modal">
              <div className="inner">
                <div
                  className="box"
                  style={{
                    width: isMobile ? "90vw" : isTablet ? "500px" : "580px",
                    padding: isMobile ? "20px" : "40px",
                  }}
                >
                  <div
                    className="flex "
                    style={{ justifyContent: "space-between" }}
                  >
                    <h5 className="color-primary f-w-400">Feedback Details</h5>
                    <CiCircleRemove
                      className="cursor-pointer transition hover-color-secondary"
                      onClick={() => setShowFeedbackDetail(false)}
                      style={{ fontSize: isMobile ? "24px" : "32px" }}
                    />
                  </div>
                  <div
                    style={{
                      padding: isMobile ? "10px 5px" : "20px 5px",
                      height: isMobile ? "70vh" : "60vh",
                      margin: "10px",
                      overflowY: "scroll",
                      overflowX: "hidden",
                      scrollbarWidth: "thin",
                    }}
                  >
                    {feedbackDetail && !loading && (
                      <div className="activity-card pos-rel">
                        <div
                          style={{
                            position: "absolute",
                            height: "4px",
                            left: "-1px",
                            right: "-1px",
                            bottom: "-2px",
                            borderBottomLeftRadius: "4px",
                            borderBottomRightRadius: "4px",
                          }}
                        />
                        <h6
                          className="f-w-700"
                          style={{
                            paddingBottom: "5px",
                            fontSize: isMobile ? "14px" : "16px",
                          }}
                        >
                          <span
                            className="f-w-300"
                            style={{ fontWeight: "bold" }}
                          >
                            Subject:
                          </span>{" "}
                          {feedbackDetail?.subject}
                        </h6>
                        <div style={{ padding: "12px 0" }}>
                          <div
                            className="info-row flex "
                            style={{
                              gap: "10px",
                              paddingBottom: "10px",
                              flexDirection: isMobile ? "column" : "row",
                            }}
                          >
                            <InfoItem
                              icon={
                                <LuUserRoundCheck
                                  size={20}
                                  className="color-primary"
                                />
                              }
                              label="Customer Name"
                              name={feedbackDetail?.user?.fullName || "-"}
                            />
                            <InfoItem
                              icon={
                                <LuMailCheck
                                  size={20}
                                  className="color-primary"
                                />
                              }
                              label="Email"
                              name={feedbackDetail?.user?.email || "-"}
                            />
                          </div>
                          <div
                            className="info-row flex"
                            style={{
                              gap: "10px",
                              paddingBottom: "10px",
                              flexDirection: isMobile ? "column" : "row",
                            }}
                          >
                            <InfoItem
                              icon={
                                feedbackDetail?.feedbackStatusID === 1 ? (
                                  <LuBadgeAlert
                                    size={20}
                                    className="color-primary"
                                  />
                                ) : (
                                  <LuBadgeCheck
                                    size={20}
                                    className="color-primary"
                                  />
                                )
                              }
                              label="Feedback Status"
                              name={
                                feedbackDetail?.feedbackStatusID === 1
                                  ? "Open"
                                  : "Resolved"
                              }
                            />
                            <InfoItem
                              icon={
                                <LuAppWindowMac
                                  size={20}
                                  className="color-primary"
                                />
                              }
                              label="Category"
                              name={
                                filterOptions.category[feedbackDetail?.category]
                                  ?.label || "-"
                              }
                            />
                          </div>
                          <div
                            className="info-row flex "
                            style={{
                              gap: "10px",
                              paddingBottom: "10px",
                              flexDirection: isMobile ? "column" : "row",
                            }}
                          >
                            <InfoItem
                              icon={
                                <LuCalendarSearch
                                  size={20}
                                  className="color-primary"
                                />
                              }
                              label="Created At"
                              name={
                                feedbackDetail?.createdAt
                                  ? formatDateTime(feedbackDetail.createdAt)
                                  : "-"
                              }
                            />
                            <InfoItem
                              icon={
                                <LuCalendarCheck
                                  size={20}
                                  className="color-primary"
                                />
                              }
                              label="Resolved At"
                              name={
                                feedbackDetail?.updatedAt
                                  ? formatDateTime(feedbackDetail.updatedAt)
                                  : "-"
                              }
                            />
                          </div>
                          <hr style={{ marginBottom: "12px" }} />
                          <InfoItem
                            icon={
                              <LuMessageCircleCode
                                size={20}
                                className="color-primary"
                              />
                            }
                            showfulltext
                            label="Query"
                            name={feedbackDetail?.description || "-"}
                          />
                          <InfoItem
                            icon={
                              <LuRadio size={20} className="color-primary" />
                            }
                            showfulltext
                            label="Response"
                            name={feedbackDetail?.response || "-"}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <ButtonLightTextIcon
                    title="Cancel"
                    onClick={() => setShowFeedbackDetail(false)}
                    disabled={loading}
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="26.562"
                        height="17.809"
                        viewBox="0 0 26.562 17.809"
                      >
                        <path d="M0,8.88,10.686,0l1.937,2.293L6.453,7.4H26.562v3H6.454l6.166,5.108-1.934,2.3Z" />
                      </svg>
                    }
                  />
                </div>
              </div>
            </section>
          </PopUpModal>
        )}
      </section>
    </section>
  );
};

const FeedbackModal = ({ id, onClose, callback }) => {
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const {
    setNewNotification,
    setShowNotification,
    addNotification,
    addToastMessage,
  } = useUI();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleResolve = useCallback(
    async (response) => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/1.0.0/feedback/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
          body: JSON.stringify({ response, feedbackStatusID: 2 }),
        });
        const result = await res.json();

        if (res.ok && result.result.responseCode === 200) {
          addToastMessage({
            status: true,
            message: "You have successfully Resolved the feedback.",
          });
          setNewNotification(true);
          setShowNotification(false);
          addNotification({
            id: Date.now(),
            message: "You have successfully Resolved the feedback.",
            status: true,
            archive: false,
          });
          onClose();
          callback();
        } else {
          addToastMessage({
            status: false,
            message: "Something went wrong while resolving feedback.",
          });
        }
      } catch (error) {
        console.error("Error:", error);
        addToastMessage({
          status: false,
          message: "Something went wrong while resolving feedback.",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      id,
      onClose,
      callback,
      addToastMessage,
      addNotification,
      setNewNotification,
      setShowNotification,
    ]
  );

  const isMobile = windowWidth < 768;

  return (
    <PopUpModal>
      <Formik
        initialValues={{ response: "" }}
        validate={({ response }) =>
          response.trim() ? {} : { response: "Response is required." }
        }
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          handleResolve(values.response).finally(() => setSubmitting(false));
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit}>
            <section id="rs-modal">
              <div className="inner">
                <div
                  className="box"
                  style={{
                    width: isMobile ? "90vw" : "480px",
                    padding: isMobile ? "20px" : "40px",
                  }}
                >
                  <div
                    className="flex "
                    style={{
                      justifyContent: "space-between",
                    }}
                  >
                    <h4
                      className="color-primary"
                      style={{ fontSize: isMobile ? "18px" : "24px" }}
                    >
                      Resolve Query
                    </h4>
                    <CiCircleRemove
                      className="cursor-pointer transition hover-color-secondary"
                      onClick={onClose}
                      style={{ fontSize: isMobile ? "24px" : "32px" }}
                    />
                  </div>
                  <br />
                  <Field
                    as="textarea"
                    placeholder="Enter your response"
                    name="response"
                    className={`textarea ${
                      errors.response && touched.response ? "error" : ""
                    }`}
                    value={values.response}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={loading}
                    style={{
                      width: "100%",
                      minHeight: isMobile ? "100px" : "120px",
                    }}
                  />
                  {errors.response && touched.response && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.response}
                    </div>
                  )}
                  <br />
                  <div
                    className="flex"
                    style={{
                      justifyContent: "space-between",
                      gap: "12px",
                      marginTop: "4px",
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <ButtonLightTextIcon
                      title="Cancel"
                      onClick={onClose}
                      disabled={loading}
                      type="button"
                      icon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="26.562"
                          height="17.809"
                          viewBox="0 0 26.562 17.809"
                        >
                          <path d="M0,8.88,10.686,0l1.937,2.293L6.453,7.4H26.562v3H6.454l6.166,5.108-1.934,2.3Z" />
                        </svg>
                      }
                    />
                    <ButtonTextIcon
                      title={loading ? "Resolving..." : "Query Resolved"}
                      icon={<CiCircleCheck />}
                      type="submit"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </section>
          </Form>
        )}
      </Formik>
    </PopUpModal>
  );
};

export default FeedBack;
